import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useCollection } from '@nandorojo/swr-firestore';

import { IphotocardPostingItem } from '@typings/photocard/photocardPosting.interface';

const Section = styled.section`
  width: 100%;
  max-width: 720px;
  margin: 80px auto 0;
`;

const ListBox = styled.div`
  overflow: hidden;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);
  border-radius: var(--cardBorderRadius);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 12px;
  border-bottom: 1px solid var(--lineColor);

  span {
    flex-shrink: 0;
    color: var(--subTextColor);
    font-size: 13px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const activeStyle = css`
  background: var(--buttonBackgroundColor);
  color: var(--buttonTextColor);

  .episode {
    color: var(--buttonTextColor);
  }
`;

const Item = styled(Link)<{ active: boolean; hasThumbnail: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasThumbnail }) =>
    hasThumbnail ? '28px 44px minmax(0, 1fr)' : '28px minmax(0, 1fr)'};
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid var(--lineColor);
  transition: background-color 0.2s, color 0.2s;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--inkColor);
  }

  ${({ active }) => active && activeStyle}

  @media (max-width: 480px) {
    grid-template-columns: ${({ hasThumbnail }) =>
      hasThumbnail ? '24px 40px minmax(0, 1fr)' : '24px minmax(0, 1fr)'};
    gap: 6px;
    padding: 8px;
  }
`;

const Episode = styled.span`
  color: var(--subTextColor);
  font-size: 13px;
  font-weight: 700;
  text-align: center;
`;

const Thumbnail = styled.div<{ src?: string }>`
  width: 44px;
  height: 40px;
  overflow: hidden;
  background-color: var(--backgroundColor);
  background-image: ${({ src }) => (src ? `url('${src}')` : 'none')};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border: 1px solid var(--lineColor);
  border-radius: var(--cardBorderRadius);

  @media (max-width: 480px) {
    width: 40px;
    height: 36px;
  }
`;

const Content = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 13px;
  }

  span {
    margin-top: 2px;
    color: var(--subTextColor);
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  padding: 48px 20px;
  color: var(--subTextColor);
  text-align: center;
`;

type Props = {
  collectionPath: string;
  currentDocId: string;
  photocardId: string;
  photocardClosetId: string;
};

function PhotocardLogNavigation({
  collectionPath,
  currentDocId,
  photocardId,
  photocardClosetId,
}: Props) {
  const { data: logList } = useCollection<IphotocardPostingItem>(
    collectionPath,
    {
      listen: true,
      where: ['photocardClosetId', '==', photocardClosetId],
      orderBy: ['order', 'asc'],
    },
  );

  const stringQuery = `?${new URLSearchParams({
    photocardId,
    photocardClosetId,
  }).toString()}`;

  const hasRoot = Boolean(photocardId && photocardClosetId);

  return (
    <Section aria-label="캐릭터 로그 목록">
      <ListBox>
        <Header>
          <span>총 {hasRoot ? logList?.length || 0 : 0}화</span>
        </Header>

        {!hasRoot ? (
          <EmptyState>캐릭터 로그 경로를 확인할 수 없습니다.</EmptyState>
        ) : logList && logList.length > 0 ? (
          <List>
            {logList.map((item, index) => {
              if (!item.id) return null;

              const thumbnail =
                item.thumbnailURL || item.imgURLList?.[0] || '';

              return (
                <Item
                  key={item.id}
                  to={`/${collectionPath}/${item.id}${stringQuery}`}
                  active={item.id === currentDocId}
                  hasThumbnail={!!thumbnail}
                  aria-current={item.id === currentDocId ? 'page' : undefined}
                >
                  <Episode className="episode">{index + 1}</Episode>
                  {thumbnail && <Thumbnail src={thumbnail} />}
                  <Content>
                    <strong>{item.title || '제목 없음'}</strong>
                    {item.subTitle && <span>{item.subTitle}</span>}
                  </Content>
                </Item>
              );
            })}
          </List>
        ) : (
          <EmptyState>등록된 캐릭터 로그가 없습니다.</EmptyState>
        )}
      </ListBox>
    </Section>
  );
}

export default React.memo(PhotocardLogNavigation);
